/**
 * Scope options for the "edit a recurring message" dialog.
 *
 * Lives in its own module so consumers (the calendar grid, the delete button)
 * can use it without importing the calendar view — which pulls in the calendar
 * library's stylesheets as a side effect.
 *
 * Order matters: a DRAG offers only the first two (`slice(0, 2)`), because
 * moving every occurrence of a series is not something a drag should mean.
 * Deleting offers all three.
 */
export type confirmationDialogOption =
    | "All messages"
    | "This message"
    | "This and following messages";

export const confirmationDialogOptions: confirmationDialogOption[] = [
    "This message",
    "This and following messages",
    "All messages",
];

/**
 * Display labels for the scope options.
 *
 * The VALUES above are a wire format: they are compared by string in
 * `CalendarThunks.mapDialogOptionToScope`, in `edit-recurring-dialog-confirmation`
 * and in `recurring-delete-scope`. So they must never change. What the user reads
 * is decoupled here — the option rows describe the OUTCOME ("The whole series")
 * instead of the data model ("All messages"), while the value handed back through
 * `onClose` stays byte-identical.
 *
 * `MessageConfirmationDialog` looks each option up here; anything not in the map
 * (e.g. an ad-hoc option list) renders its own text as before.
 */
export const recurringScopeLabelKeys: Record<confirmationDialogOption, string> = {
    "This message": "recurringScope.onlyThisOne",
    "This and following messages": "recurringScope.thisAndAfter",
    "All messages": "recurringScope.wholeSeries",
};

/** English fallbacks, used when a locale file has no entry for the key. */
export const recurringScopeLabelFallbacks: Record<confirmationDialogOption, string> = {
    "This message": "Only this one",
    "This and following messages": "This one and all the ones after it",
    "All messages": "The whole series",
};

/**
 * The least-destructive option, named rather than indexed.
 *
 * Its POSITION in the list is a layout decision; its identity is a safety one.
 * A surface that wants "pre-select the safe answer" must ask for this, so a
 * later re-ordering of the list cannot silently pre-select "The whole series".
 */
export const THIS_MESSAGE_OPTION: confirmationDialogOption = "This message";

/**
 * The dialog value translated into the scope `RRuleRecurrenceHandler` speaks.
 *
 * Lives beside the values it maps because it is part of the same wire format.
 * It used to be a private const inside CalendarThunks, which meant a test that
 * wanted to drive the delete path from an option string had to re-implement it —
 * and a re-implemented mapping is a test that agrees with itself.
 */
export function scopeForOption(value: confirmationDialogOption): 'all' | 'this' | 'thisAndFollowing' {
    if (value === "All messages") return "all";
    if (value === "This message") return "this";
    return "thisAndFollowing";
}
